import React from "react";
import { Select, SelectProps } from "@mantine/core";
import { AppIcon } from "../AppIcon";

const IMPORTANCE_OPTIONS = [
  {
    value: "100",
    label: "High Importance",
    icon: "Check",
    color: "var(--mantine-color-red-6)",
    description: "This message will be visually distinct",
  },
  {
    value: "0",
    label: "Normal Importance",
    icon: "Check",
    color: "var(--mantine-color-green-6)",
    description: "Normal importance",
  },
] as const;

export type ImportanceValue = 100 | 0;

export interface ImportanceSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Current status value (100 for enabled, 0 for disabled) */
  value: number | null | undefined;

  /** Callback when status changes */
  onChange: (value: number | null) => void;

  /** Whether to show icons in the dropdown */
  withIcons?: boolean;

  /** Whether to show descriptions in the dropdown */
  withDescriptions?: boolean;
}

export const ImportanceSelector: React.FC<ImportanceSelectorProps> = ({
  value,
  onChange,
  withIcons = true,
  withDescriptions = false,
  placeholder = "Select importance",
  ...selectProps
}) => {
  // Convert numeric value to string for Select component
  const stringValue = value !== null ? String(value) : null;

  // Prepare select data with optional descriptions
  const selectData = IMPORTANCE_OPTIONS.map((option) => {
    let label: string = option.label;

    if (withDescriptions) {
      label = `${option.label} - ${option.description}`;
    }

    return {
      value: option.value,
      label,
    };
  });

  // Handle change and convert back to number
  const handleChange = (stringValue: string | null) => {
    if (stringValue === null) {
      onChange(null);
    } else {
      const numValue = Number(stringValue) as ImportanceValue;
      onChange(numValue);
    }
  };

  // Get current option for icon display
  const currentOption = IMPORTANCE_OPTIONS.find(
    (opt) => opt.value === stringValue
  );

  const leftSection =
    withIcons && currentOption ? (
      <AppIcon
        iconName={currentOption.icon as any}
        size={16}
        style={{
          color: currentOption.color,
        }}
      />
    ) : undefined;

  return (
    <Select
      {...selectProps}
      data={selectData}
      value={stringValue}
      onChange={handleChange}
      placeholder={placeholder}
      leftSection={leftSection}
      clearable={false}
    />
  );
};

// Export status options for use in other components
export { IMPORTANCE_OPTIONS };
